/**
 * ProductivityCard - Afișează volumetrie și productivitate medic
 */

import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Image as ImageIcon,
} from '@mui/icons-material'
import { formatNumber } from '../../hooks/useAnalytics'

interface ProductivityCardProps {
  totalStudies: number
  completedStudies: number
  totalImages: number
  complexityWeightedVolume: number
  studiesPerHour: number
  imagesPerHour: number
  modalityDistribution?: Record<string, number>
  priorityDistribution?: Record<string, number>
  loading?: boolean
}

export const ProductivityCard: React.FC<ProductivityCardProps> = ({
  totalStudies,
  completedStudies,
  totalImages,
  complexityWeightedVolume,
  studiesPerHour,
  imagesPerHour,
  modalityDistribution,
  priorityDistribution,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  const completionRate = totalStudies > 0 ? (completedStudies / totalStudies) * 100 : 0

  return (
    <Card elevation={1} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', overflow: 'auto' }}>
        {/* Header - Compact */}
        <Box display="flex" alignItems="center" mb={1}>
          <SpeedIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Productivitate</Typography>
        </Box>

        {/* Stats principale - Compact */}
        <Grid container spacing={1} mb={1}>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main' }}>{formatNumber(totalStudies)}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Total Studii</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'success.main' }}>{formatNumber(completedStudies)}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Completate</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, color: 'info.main' }}>{formatNumber(totalImages)}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Imagini</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, color: 'success.main' }}>{completionRate.toFixed(0)}%</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Completare</Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        {/* Eficiență - Compact */}
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Eficiență:</Typography>
        <Box mb={1}>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}><AssignmentIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />Studii/oră:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{studiesPerHour.toFixed(1)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}><ImageIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />Imagini/oră:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{formatNumber(Math.round(imagesPerHour))}</Typography>
          </Box>
        </Box>

        {/* Distribuție Modalități - Compact */}
        {modalityDistribution && Object.keys(modalityDistribution).length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Modalități:</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {Object.entries(modalityDistribution).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([modality, count]) => (
                <Chip key={modality} label={`${modality}: ${count}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />
              ))}
            </Box>
          </>
        )}

        {/* Distribuție Priorități - Compact */}
        {priorityDistribution && Object.keys(priorityDistribution).length > 0 && (
          <Box mt={1} display="flex" gap={0.5}>
            {priorityDistribution.urgent > 0 && <Chip label={`Urg: ${priorityDistribution.urgent}`} color="error" size="small" sx={{ height: 18, fontSize: '0.68rem' }} />}
            {priorityDistribution.normal > 0 && <Chip label={`Norm: ${priorityDistribution.normal}`} color="default" size="small" sx={{ height: 18, fontSize: '0.68rem' }} />}
          </Box>
        )}

        {/* Volumetrie Ponderată - Compact */}
        {complexityWeightedVolume > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Vol. ponderată:</Typography>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 500 }}>{complexityWeightedVolume.toFixed(1)}</Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}

